import { computed, inject, ref, type ComputedRef, type InjectionKey } from 'vue'

export type ToastType = 'default' | 'success' | 'info' | 'warning' | 'error' | 'loading'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  title: string
  /** A second line. Text, never markup — see the note in use-confirm.ts. */
  description?: string
  type?: ToastType
  /** Milliseconds. `null` keeps it up until something dismisses it. */
  duration?: number | null
  action?: ToastAction
  /** Reuse an existing toast instead of adding one — how `promise` updates. */
  id?: number
}

export interface ToastEntry {
  id: number
  title: string
  description?: string
  type: ToastType
  duration: number | null
  action?: ToastAction
  /** Bumped on every update, so the host can restart a timer it already ran. */
  revision: number
}

const DEFAULT_DURATION: Record<ToastType, number | null> = {
  default: 4000,
  success: 4000,
  info: 4000,
  warning: 6000,
  error: 8000,
  loading: null,
}

const entries = ref<ToastEntry[]>([])
let nextId = 0

/** Newest first: the front of the stack is index 0. */
export const toasts: ComputedRef<ToastEntry[]> = computed(() => entries.value)

export const TOAST_DISMISS_KEY: InjectionKey<(id: number) => void> = Symbol('ms-toast-dismiss')

export const useToastDismiss = (): ((id: number) => void) =>
  inject(TOAST_DISMISS_KEY, dismissToast)

export const dismissToast = (id: number): void => {
  entries.value = entries.value.filter((entry) => entry.id !== id)
}

export const dismissAllToasts = (): void => {
  entries.value = []
}

const upsert = (options: ToastOptions): number => {
  const type = options.type ?? 'default'
  const duration = options.duration === undefined ? DEFAULT_DURATION[type] : options.duration

  if (options.id !== undefined) {
    const existing = entries.value.find((entry) => entry.id === options.id)
    if (existing) {
      entries.value = entries.value.map((entry) =>
        entry.id === options.id
          ? {
              ...entry,
              title: options.title,
              description: options.description,
              type,
              duration,
              action: options.action,
              revision: entry.revision + 1,
            }
          : entry,
      )
      return options.id
    }
  }

  const id = options.id ?? ++nextId
  entries.value = [
    {
      id,
      title: options.title,
      description: options.description,
      type,
      duration,
      action: options.action,
      revision: 0,
    },
    ...entries.value,
  ]
  return id
}

type Shorthand = (title: string, options?: Omit<ToastOptions, 'title' | 'type'>) => number

interface ToastApi {
  (options: ToastOptions): number
  success: Shorthand
  info: Shorthand
  warning: Shorthand
  error: Shorthand
  loading: Shorthand
  dismiss: (id?: number) => void
  /** One toast for the whole of an async task: loading while it runs. */
  promise: <T>(
    task: Promise<T>,
    messages: {
      loading: string
      success: string | ((value: T) => string)
      error: string | ((reason: unknown) => string)
    },
  ) => Promise<T>
}

const base = (options: ToastOptions): number => upsert(options)

const shorthand =
  (type: ToastType): Shorthand =>
  (title, options) =>
    upsert({ ...options, title, type })

export const toast: ToastApi = Object.assign(base, {
  success: shorthand('success'),
  info: shorthand('info'),
  warning: shorthand('warning'),
  error: shorthand('error'),
  loading: shorthand('loading'),
  dismiss: (id?: number): void => {
    if (id === undefined) dismissAllToasts()
    else dismissToast(id)
  },
  promise: async <T>(
    task: Promise<T>,
    messages: {
      loading: string
      success: string | ((value: T) => string)
      error: string | ((reason: unknown) => string)
    },
  ): Promise<T> => {
    const id = upsert({ title: messages.loading, type: 'loading' })
    try {
      const value = await task
      upsert({
        id,
        type: 'success',
        title:
          typeof messages.success === 'function' ? messages.success(value) : messages.success,
      })
      return value
    } catch (reason) {
      upsert({
        id,
        type: 'error',
        title: typeof messages.error === 'function' ? messages.error(reason) : messages.error,
      })
      throw reason
    }
  },
})
